/**
 * GithubTeamPicker - Custom Backstage Scaffolder Field Extension
 *
 * A dropdown picker that lists GitHub teams from an organization.
 *
 * Usage in template.yaml:
 *   ui:field: GithubTeamPicker
 *   ui:options:
 *     owner: badal-io    # GitHub organization
 *     pattern: '*'       # Optional: glob pattern to filter team names
 */

import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useGithubTeams, GithubTeam } from './shared';

export interface GithubTeamPickerOptions {
  owner?: string;
  pattern?: string;
  [key: string]: string | number | boolean | object | any[] | null | undefined;
}

export const GithubTeamPicker = (
  props: FieldExtensionComponentProps<string, GithubTeamPickerOptions>,
) => {
  const { onChange, rawErrors, required, formData, uiSchema, schema } = props;

  const options = uiSchema?.['ui:options'];
  const owner = options?.owner || 'badal-io';
  const pattern = options?.pattern || '*';

  const { teams, loading, error } = useGithubTeams(owner, pattern);

  const handleChange = (_event: React.ChangeEvent<{}>, value: GithubTeam | null) => {
    onChange(value?.slug || '');
  };

  const selectedTeam = teams.find(t => t.slug === formData) || null;

  if (loading) {
    return (
      <FormControl fullWidth margin="normal">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CircularProgress size={20} />
          <span>Loading teams...</span>
        </div>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth margin="normal" error>
        <InputLabel>{schema.title || 'Team'}</InputLabel>
        <Select disabled value="">
          <MenuItem value="">Error loading teams</MenuItem>
        </Select>
        <FormHelperText>{error}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth margin="normal" required={required} error={rawErrors && rawErrors.length > 0}>
      <Autocomplete
        id="github-team-picker"
        options={teams}
        getOptionLabel={(option) => option.name}
        getOptionSelected={(option, value) => option.slug === value.slug}
        value={selectedTeam}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label={schema.title || 'Team'}
            required={required}
            helperText={
              (rawErrors && rawErrors.length > 0 && rawErrors.join(', ')) ||
              schema.description ||
              uiSchema?.['ui:help']
            }
            error={rawErrors && rawErrors.length > 0}
            variant="outlined"
          />
        )}
        renderOption={(option) => (
          <div>
            <div style={{ fontWeight: 500 }}>{option.name}</div>
            {option.description && (
              <div style={{ fontSize: '0.75rem', color: '#888' }}>
                {option.description}
              </div>
            )}
          </div>
        )}
      />
    </FormControl>
  );
};

export default GithubTeamPicker;
